import { Navigation } from '../navigation.js'
import { useSortingVisualizer } from './useSortingVisualizer.js'
import { ALGORITHMS } from './algorithms.js'
import CodeViewer from './CodeViewer.js'
import { Play, Pause, StepForward, Square, Shuffle, Volume2, VolumeX } from 'lucide-react'

function barClass(index, comparing, swapping, sortedIndices) {
  if (swapping.includes(index)) return 'bg-red-500'
  if (comparing.includes(index)) return 'bg-yellow-400'
  if (sortedIndices.has(index)) return 'bg-green-500'
  return 'bg-blue-500'
}

export default function SortingVisualizer() {
  const {
    array,
    size,
    speed,
    algorithmKey,
    comparing,
    swapping,
    sortedIndices,
    isSorting,
    isDone,
    currentLine,
    soundOn,
    newArray,
    start,
    pause,
    step,
    stop,
    setAlgorithm,
    setSpeed,
    toggleSound,
  } = useSortingVisualizer()

  const maxValue = Math.max(...array, 1)
  const algorithm = ALGORITHMS.find((algo) => algo.key === algorithmKey)

  return (
    <div>
      <Navigation />

      <div className="page-tailwind">
        <h1 className="header">Sorting Visualizer</h1>

        <div className="flex flex-wrap items-end gap-6 my-6">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-700">Algorithm</span>
            <select
              className="input-field"
              value={algorithmKey}
              onChange={(e) => setAlgorithm(e.target.value)}
              disabled={isSorting}
            >
              {ALGORITHMS.map((algo) => (
                <option key={algo.key} value={algo.key}>
                  {algo.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-700">Size: {size}</span>
            <input
              type="range"
              min="5"
              max="120"
              value={size}
              disabled={isSorting}
              onChange={(e) => newArray(Number(e.target.value))}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-medium text-slate-700">Speed: {speed}</span>
            <input
              type="range"
              min="1"
              max="100"
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
            />
          </label>

          <div className="flex items-center gap-2">
            <button
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => newArray()}
              disabled={isSorting}
            >
              <Shuffle className="w-4 h-4" /> New Array
            </button>

            {isSorting ? (
              <button className="btn-primary inline-flex items-center gap-2" onClick={pause}>
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button className="btn-primary inline-flex items-center gap-2" onClick={start}>
                <Play className="w-4 h-4" /> Start
              </button>
            )}

            <button className="btn-secondary inline-flex items-center gap-2" onClick={step} disabled={isSorting}>
              <StepForward className="w-4 h-4" /> Step
            </button>

            <button className="btn-secondary inline-flex items-center gap-2" onClick={stop}>
              <Square className="w-4 h-4" /> Stop
            </button>

            <button
              className="btn-secondary inline-flex items-center gap-2"
              onClick={toggleSound}
              title={soundOn ? 'Mute sounds' : 'Enable sounds'}
              aria-label={soundOn ? 'Mute sounds' : 'Enable sounds'}
            >
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex items-end gap-px h-[60vh] bg-white rounded-lg border border-slate-200 p-2 flex-1 min-w-0">
            {array.map((value, index) => (
              <div
                key={index}
                className={`${barClass(index, comparing, swapping, sortedIndices)} flex-1 rounded-t transition-all duration-100 ease-linear`}
                style={{ height: `${(value / maxValue) * 100}%` }}
              />
            ))}
          </div>

          <div className="w-full lg:w-[440px] shrink-0">
            <CodeViewer
              title={algorithm?.label ?? 'Code'}
              code={algorithm?.code}
              currentLine={currentLine}
            />
          </div>
        </div>

        <p className="mt-4 text-slate-600">
          {isDone ? 'Sorted!' : isSorting ? 'Sorting…' : 'Press Start to sort the array.'}
        </p>
      </div>
    </div>
  )
}
